import React, { useEffect, useState } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { FileText, ArrowLeft } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import { marketplaceAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';

/**
 * Dedicated landlord signup form.
 *
 * Two modes:
 *  - Anonymous: classic signup, collects email + password too.
 *  - Authed (add-role): hides email + password and tags the new role
 *    onto the existing account via the auth-aware backend endpoint.
 *
 * Mirrors DriverSignupPage / RestaurantSignupPage so anyone landing
 * here from /add-role gets a consistent dedicated experience instead
 * of being thrown back to the generic /signup page.
 */
const LandlordSignupPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  const { login, user: authedUser, refreshUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const isAddRoleMode = !!authedUser;

  const [form, setForm] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    phone: '',
    companyName: '',
  });

  // Preserve any referral code from the URL.
  useEffect(() => {
    const refCode = new URLSearchParams(location.search).get('ref');
    if (refCode) localStorage.setItem('bc_ref_code', refCode.trim().toUpperCase());
  }, [location.search]);

  // Prefill the name from the authed user in add-role mode.
  useEffect(() => {
    if (isAddRoleMode && authedUser?.name && !form.name) {
      setForm(prev => ({ ...prev, name: authedUser.name }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAddRoleMode]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!isAddRoleMode) {
      if (form.password !== form.confirmPassword) {
        toast({ title: 'Error', description: 'Passwords do not match', variant: 'destructive' });
        return;
      }
      if (!form.name || !form.email || !form.password || !form.phone) {
        toast({ title: 'Error', description: 'Please fill in all required fields', variant: 'destructive' });
        return;
      }
    } else if (!form.name || !form.phone) {
      toast({ title: 'Error', description: 'Please fill in all required fields', variant: 'destructive' });
      return;
    }

    setLoading(true);
    try {
      await marketplaceAPI.registerLandlord({
        name: form.name,
        email: isAddRoleMode ? authedUser.email : form.email,
        password: isAddRoleMode ? 'addrole-placeholder-ignored' : form.password,
        phone: form.phone,
        companyName: form.companyName || null,
        referredBy: localStorage.getItem('bc_ref_code') || undefined,
      });

      if (isAddRoleMode) {
        toast({ title: 'Landlord role added', description: 'Welcome to the landlord dashboard.' });
        try { await refreshUser?.(); } catch (_) {}
      } else {
        toast({ title: 'Success', description: 'Landlord account created. Logging you in…' });
        await login(form.email, form.password);
      }
      navigate('/landlord/dashboard');
    } catch (err) {
      toast({
        title: 'Registration failed',
        description: err.response?.data?.detail || 'Could not register landlord',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F4F2E9] flex items-center justify-center p-4">
      <Card className="w-full max-w-lg">
        <CardHeader className="text-center">
          <Button variant="ghost" size="icon" className="absolute left-4 top-4" onClick={() => navigate(-1)}>
            <ArrowLeft size={20} />
          </Button>
          <div className="mx-auto mb-4 p-3 bg-emerald-600 rounded-full w-fit">
            <FileText size={32} className="text-white" />
          </div>
          <CardTitle className="text-2xl text-[#1F3A68]">
            {isAddRoleMode ? 'Add Landlord role' : 'Landlord Signup'}
          </CardTitle>
          <CardDescription>List long let properties for Rent-to-Rent deals</CardDescription>
        </CardHeader>
        <CardContent>
          {isAddRoleMode && (
            <div className="mb-4 p-3 bg-[#1F3A68]/5 border border-[#1F3A68]/20 rounded-lg text-sm text-[#1F3A68]">
              <p className="font-semibold">Adding Landlord role to your existing account</p>
              <p className="text-xs text-[#1F3A68]/80 mt-0.5 break-all">Linked to {authedUser?.email}.</p>
            </div>
          )}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label>Full Name *</Label>
              <Input
                value={form.name}
                onChange={e => setForm(p => ({ ...p, name: e.target.value }))}
                placeholder="Jane Smith"
              />
            </div>

            {isAddRoleMode ? (
              <div>
                <Label>Phone *</Label>
                <Input
                  value={form.phone}
                  onChange={e => setForm(p => ({ ...p, phone: e.target.value }))}
                  placeholder="+44 123 456 7890"
                />
              </div>
            ) : (
              <>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Email *</Label>
                    <Input
                      type="email"
                      value={form.email}
                      onChange={e => setForm(p => ({ ...p, email: e.target.value }))}
                      placeholder="you@example.com"
                    />
                  </div>
                  <div>
                    <Label>Phone *</Label>
                    <Input
                      value={form.phone}
                      onChange={e => setForm(p => ({ ...p, phone: e.target.value }))}
                      placeholder="+44 123 456 7890"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Password *</Label>
                    <Input
                      type="password"
                      value={form.password}
                      onChange={e => setForm(p => ({ ...p, password: e.target.value }))}
                      placeholder="••••••••"
                    />
                  </div>
                  <div>
                    <Label>Confirm Password *</Label>
                    <Input
                      type="password"
                      value={form.confirmPassword}
                      onChange={e => setForm(p => ({ ...p, confirmPassword: e.target.value }))}
                      placeholder="••••••••"
                    />
                  </div>
                </div>
              </>
            )}

            <div>
              <Label>Company Name (optional)</Label>
              <Input
                value={form.companyName}
                onChange={e => setForm(p => ({ ...p, companyName: e.target.value }))}
                placeholder="If you let through a limited company"
              />
              <p className="text-xs text-gray-500 mt-1">Leave blank if you let properties as an individual.</p>
            </div>

            <div className="flex items-start gap-2">
              <input type="checkbox" required className="w-4 h-4 rounded border-gray-300 mt-0.5" />
              <span className="text-xs text-gray-600">
                I agree to the{' '}
                <a href="/terms" target="_blank" rel="noopener noreferrer" className="text-[#1F3A68] underline">Terms &amp; Conditions</a>
                {' '}and{' '}
                <a href="/privacy" target="_blank" rel="noopener noreferrer" className="text-[#1F3A68] underline">Privacy Policy</a>.
              </span>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-emerald-600 hover:bg-emerald-700 text-white h-12 text-base"
            >
              {loading ? (isAddRoleMode ? 'Adding role…' : 'Creating account…') : (isAddRoleMode ? 'Add Landlord role' : 'Create Landlord Account')}
            </Button>

            {!isAddRoleMode && (
              <p className="text-xs text-center text-gray-600">
                Already have an account?{' '}
                <Link to="/login" className="text-[#1F3A68] font-semibold underline">Log in</Link>
              </p>
            )}
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default LandlordSignupPage;
